// AETHER — Local-first engine. Works beautifully offline and in the field.
// All private data stays on device until explicit artifact forge + optional sync.
// This makes the instrument usable during real civic emergencies (connectivity is unreliable).

import { nanoid } from 'nanoid'
import type { Kit, Participant, Node, ClarityCard, Artifact, NodeWithChildren, CalibrationGraphData, ExperimentVariant, Basis } from '../types'

const KITS_KEY = 'aether:kits'
const CURRENT_KIT_KEY = 'aether:currentKit'
const PARTICIPANT_KEY = 'aether:participant' // stable id + per-kit names
const NODES_KEY = 'aether:nodes' // kitId -> nodes[]
const CLARITY_KEY = 'aether:clarity'
const ARTIFACTS_KEY = 'aether:artifacts'
const EVENTS_KEY = 'aether:events'

function load<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) : fallback
  } catch {
    return fallback
  }
}
function save<T>(key: string, val: T) {
  localStorage.setItem(key, JSON.stringify(val))
}

export function getStableParticipantId(): string {
  let id = localStorage.getItem(PARTICIPANT_KEY + ':id')
  if (!id) {
    id = 'p_' + nanoid(10)
    localStorage.setItem(PARTICIPANT_KEY + ':id', id)
  }
  return id
}

export function getDisplayNameForKit(kitId: string): string {
  const map = load<Record<string, string>>(PARTICIPANT_KEY + ':names', {})
  return map[kitId] || 'you'
}

export function setDisplayNameForKit(kitId: string, name: string) {
  const map = load<Record<string, string>>(PARTICIPANT_KEY + ':names', {})
  map[kitId] = name.trim() || 'you'
  save(PARTICIPANT_KEY + ':names', map)
}

export function listKits(): Kit[] {
  return load<Kit[]>(KITS_KEY, [])
}

export function getKit(idOrCode: string): Kit | null {
  const kits = listKits()
  return kits.find(k => k.id === idOrCode || k.code === idOrCode.toUpperCase()) || null
}

export function createLocalKit(name: string, context: string, horizon: string): Kit {
  const now = Date.now()
  const code = nanoid(6).toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6)
  const kit: Kit = {
    id: 'k_' + nanoid(10),
    code,
    name: name.trim() || 'Untitled resilience kit',
    context: context.trim(),
    horizon: horizon.trim() || 'next 12-48h',
    created_at: now,
    updated_at: now,
    status: 'open'
  }
  const kits = listKits()
  kits.unshift(kit)
  save(KITS_KEY, kits)
  // seed initial participant presence
  touchParticipant(kit.id)
  return kit
}

export function touchParticipant(kitId: string) {
  // lightweight last_seen for local UI
  const p = getStableParticipantId()
  // we don't persist full participants locally beyond name; events carry identity
}

export function setCurrentKit(id: string) {
  localStorage.setItem(CURRENT_KIT_KEY, id)
}
export function getCurrentKitId(): string | null {
  return localStorage.getItem(CURRENT_KIT_KEY)
}

export function getNodesForKit(kitId: string): Node[] {
  const all = load<Record<string, Node[]>>(NODES_KEY, {})
  return all[kitId] || []
}

export function saveNodesForKit(kitId: string, nodes: Node[]) {
  const all = load<Record<string, Node[]>>(NODES_KEY, {})
  all[kitId] = nodes
  save(NODES_KEY, all)
}

export function addNode(kitId: string, partial: Partial<Node> & { description: string; created_by: string }): Node {
  const nodes = getNodesForKit(kitId)
  const now = Date.now()
  const node: Node = {
    id: 'n_' + nanoid(10),
    kit_id: kitId,
    parent_id: partial.parent_id ?? null,
    created_by: partial.created_by,
    description: partial.description,
    confidence: partial.confidence ?? 65,
    confidence_basis: (partial.confidence_basis as Basis) ?? 'guess',
    burden_expected: partial.burden_expected ?? null,
    burden_actual: partial.burden_actual ?? null,
    status: partial.status ?? 'todo',
    abandoned_at: null,
    abandoned_reason: null,
    created_at: now,
    updated_at: now,
  }
  nodes.push(node)
  saveNodesForKit(kitId, nodes)
  appendEvent(kitId, node.created_by, node.id, 'node_created', { description: node.description, confidence: node.confidence })
  return node
}

export function updateNode(kitId: string, nodeId: string, patch: Partial<Node>): Node | null {
  const nodes = getNodesForKit(kitId)
  const idx = nodes.findIndex(n => n.id === nodeId)
  if (idx === -1) return null
  const before = { ...nodes[idx] }
  const updated: Node = { ...nodes[idx], ...patch, updated_at: Date.now() }
  nodes[idx] = updated
  saveNodesForKit(kitId, nodes)

  if (patch.confidence !== undefined && patch.confidence !== before.confidence) {
    appendEvent(kitId, updated.created_by, nodeId, 'confidence_set', {
      before: before.confidence, after: patch.confidence, basis: patch.confidence_basis ?? updated.confidence_basis
    })
  }
  if (patch.burden_actual !== undefined) {
    appendEvent(kitId, updated.created_by, nodeId, 'burden_reported', { expected: updated.burden_expected, actual: patch.burden_actual })
  }
  if (patch.status && patch.status !== before.status) {
    appendEvent(kitId, updated.created_by, nodeId, 'status_changed', { from: before.status, to: patch.status })
  }
  return updated
}

export function abandonNode(kitId: string, nodeId: string, reason: string, participantId: string): Node | null {
  const nodes = getNodesForKit(kitId)
  const idx = nodes.findIndex(n => n.id === nodeId)
  if (idx === -1) return null
  const node = nodes[idx]
  const updated: Node = {
    ...node,
    status: 'abandoned',
    abandoned_at: Date.now(),
    abandoned_reason: reason.trim(),
    updated_at: Date.now()
  }
  nodes[idx] = updated
  saveNodesForKit(kitId, nodes)
  appendEvent(kitId, participantId, nodeId, 'abandoned', { reason, previous_confidence: node.confidence })
  return updated
}

export function getClarityCards(kitId: string): ClarityCard[] {
  const all = load<Record<string, ClarityCard[]>>(CLARITY_KEY, {})
  return all[kitId] || []
}

export function addClarityCard(kitId: string, card: Omit<ClarityCard, 'id' | 'kit_id' | 'created_at'>): ClarityCard {
  const cards = getClarityCards(kitId)
  const full: ClarityCard = {
    ...card,
    id: 'c_' + nanoid(10),
    kit_id: kitId,
    created_at: Date.now()
  }
  cards.unshift(full)
  const all = load<Record<string, ClarityCard[]>>(CLARITY_KEY, {})
  all[kitId] = cards
  save(CLARITY_KEY, all)
  appendEvent(kitId, card.participant_id, card.node_id, 'clarity_captured', {
    what_became_clear: card.what_became_clear, surprise: card.surprise_level
  })
  return full
}

export function getArtifacts(kitId: string): Artifact[] {
  const all = load<Record<string, Artifact[]>>(ARTIFACTS_KEY, {})
  return (all[kitId] || []).filter(a => !a.revoked_at)
}

export function forgeArtifact(kitId: string, forgerId: string, type: Artifact['artifact_type'], title: string, snapshot: any): Artifact {
  const art: Artifact = {
    id: 'art_' + nanoid(10),
    kit_id: kitId,
    forger_id: forgerId,
    artifact_type: type,
    title: title.trim(),
    snapshot_json: JSON.stringify(snapshot),
    share_code: nanoid(8).toLowerCase(),
    created_at: Date.now(),
    revoked_at: null
  }
  const arts = getArtifacts(kitId)
  const all = load<Record<string, Artifact[]>>(ARTIFACTS_KEY, {})
  const list = all[kitId] || []
  list.unshift(art)
  all[kitId] = list
  save(ARTIFACTS_KEY, all)
  appendEvent(kitId, forgerId, null, 'artifact_forged', { type, title, share_code: art.share_code })
  return art
}

export function getArtifactByCode(code: string): Artifact | null {
  const all = load<Record<string, Artifact[]>>(ARTIFACTS_KEY, {})
  for (const kitArts of Object.values(all)) {
    const found = kitArts.find(a => a.share_code === code && !a.revoked_at)
    if (found) return found
  }
  return null
}

export function revokeArtifact(shareCode: string) {
  const all = load<Record<string, Artifact[]>>(ARTIFACTS_KEY, {})
  for (const kitId of Object.keys(all)) {
    all[kitId] = all[kitId].map(a => a.share_code === shareCode ? { ...a, revoked_at: Date.now() } : a)
  }
  save(ARTIFACTS_KEY, all)
}

export function appendEvent(kitId: string, participantId: string, nodeId: string | null, type: string, payload: any) {
  const events = load<any[]>(EVENTS_KEY, [])
  events.push({
    kit_id: kitId,
    participant_id: participantId,
    node_id: nodeId,
    type,
    payload_json: JSON.stringify(payload),
    ts: Date.now()
  })
  // keep last 800 events globally (cheap)
  if (events.length > 800) events.splice(0, events.length - 800)
  save(EVENTS_KEY, events)
}

export function getEventsForKit(kitId: string): any[] {
  return load<any[]>(EVENTS_KEY, []).filter(e => e.kit_id === kitId)
}

export function buildNodeTreeFromList(flat: Node[]): NodeWithChildren[] {
  const map = new Map<string, NodeWithChildren>()
  flat.forEach(n => map.set(n.id, { ...n, children: [] }))
  const roots: NodeWithChildren[] = []
  flat.forEach(n => {
    const node = map.get(n.id)!
    if (n.parent_id && map.has(n.parent_id)) {
      map.get(n.parent_id)!.children.push(node)
    } else {
      roots.push(node)
    }
  })
  // sort children by creation for stable order
  const sortRec = (ns: NodeWithChildren[]) => {
    ns.sort((a, b) => a.created_at - b.created_at)
    ns.forEach(n => sortRec(n.children))
  }
  sortRec(roots)
  return roots
}

export function buildNodeTree(kitId: string): NodeWithChildren[] {
  const flat = getNodesForKit(kitId)
  return buildNodeTreeFromList(flat)
}

export function computeCalibrationGraph(kitId: string): CalibrationGraphData {
  const nodes = getNodesForKit(kitId)
  const clarity = getClarityCards(kitId)
  const points = nodes.map(n => ({
    node_id: n.id,
    ts: n.updated_at || n.created_at,
    reported: n.confidence,
    realized: null, // in real use this can be backfilled after the fact
    burden: n.burden_actual ?? n.burden_expected,
    status: n.status,
    description: n.description,
    abandoned_reason: n.abandoned_reason
  })).sort((a, b) => a.ts - b.ts)

  const mean = points.length ? Math.round(points.reduce((s, p) => s + p.reported, 0) / points.length) : 0
  const abandons = nodes.filter(n => n.status === 'abandoned').length

  return {
    points,
    summary: {
      mean_confidence: mean,
      abandonments: abandons,
      clarity_cards: clarity.length,
      avg_burden_delta: null
    }
  }
}

// Stochastic variant — in pure local we rotate a few sensible ones for demo power
export function getLocalVariant(): ExperimentVariant {
  const variants: ExperimentVariant[] = [
    { probing_style: 'socratic', confidence_verb: 'bet', graph_density: 'all', auto_probe_after: 4 },
    { probing_style: 'scenario', confidence_verb: 'wager', graph_density: 'high_stakes', auto_probe_after: 3 },
    { probing_style: 'historical', confidence_verb: 'confident', graph_density: 'all', auto_probe_after: 5 },
  ]
  // deterministic per "session" but changes across reloads subtly
  const idx = Math.floor(Date.now() / (1000 * 60 * 25)) % variants.length
  return variants[idx]
}

// High-quality local "AI" suggestions — no slop, grounded, useful in the field
export function localDecompose(situation: string): Array<{ description: string; starterConfidence: number; basis: Basis; burden: number }> {
  const s = situation.toLowerCase()
  const base: Array<{ description: string; starterConfidence: number; basis: Basis; burden: number }> = []

  if (s.includes('flood') || s.includes('water') || s.includes('river')) {
    base.push(
      { description: 'Identify 3-5 households on the lowest street with mobility limits', starterConfidence: 55, basis: 'guess', burden: 3 },
      { description: 'Call or knock on the two nearest upstream neighbors for water height at their curb', starterConfidence: 70, basis: 'observed', burden: 2 },
      { description: 'Confirm which bridge or low point on 4th is still passable on foot', starterConfidence: 40, basis: 'rumor', burden: 4 },
      { description: 'Establish a 30-minute check-in window with two other group members', starterConfidence: 80, basis: 'group_knowledge', burden: 1 }
    )
  } else if (s.includes('power') || s.includes('outage') || s.includes('electric')) {
    base.push(
      { description: 'Walk the block once and note every visible extension cord or generator', starterConfidence: 65, basis: 'observed', burden: 3 },
      { description: 'Knock on doors of 4 households that usually have medical equipment', starterConfidence: 50, basis: 'group_knowledge', burden: 4 },
      { description: 'Find the one person on the block who still has a working landline or ham', starterConfidence: 35, basis: 'rumor', burden: 2 }
    )
  } else {
    base.push(
      { description: 'Name the single most time-sensitive concrete fact we do not yet have', starterConfidence: 60, basis: 'guess', burden: 2 },
      { description: 'Locate one person who has direct eyes on the situation right now', starterConfidence: 55, basis: 'guess', burden: 3 },
      { description: 'Agree on the exact time and channel for the next 3 check-ins', starterConfidence: 75, basis: 'group_knowledge', burden: 1 }
    )
  }
  return base.slice(0, 5)
}

export function localProbes(nodeDesc: string, variant: ExperimentVariant): string[] {
  const style = variant.probing_style
  if (style === 'socratic') {
    return [
      'What specific observation in the next 20 minutes would move your confidence by 15 points or more?',
      'Name one assumption that, if false, would make this action irrelevant or harmful.',
      'Who outside this group would be surprised by the number you just gave?'
    ]
  }
  if (style === 'scenario') {
    return [
      'Imagine the water (or outage) is 30% worse than we think in 90 minutes. What changes about this task?',
      'If this micro-task is the last thing we get to do before comms fail, is it still the right one?',
      'What would a slightly more exhausted version of us get wrong here?'
    ]
  }
  return [
    'In the last three similar situations this group faced, what did we discover only after we had already committed resources?',
    'Which nearby group or neighbor has seen a version of this before and might have different numbers?',
    'What small signal would tell us this is working faster than we expect?'
  ]
}

// Seed a rich demo kit matching the exact user narrative (flash flood, Riverbend)
export function seedDemoFloodKit(): { kit: Kit; nodes: Node[]; clarity: ClarityCard[] } {
  const kit = createLocalKit(
    'Riverbend mutual aid — water rising',
    'Flash flood watch. River at 5.8ft and climbing. 4 households with known mobility needs on lowest block. Power still on.',
    'next 6–18 hours'
  )
  const pid = getStableParticipantId()
  setDisplayNameForKit(kit.id, 'Maya')

  const n1 = addNode(kit.id, { description: 'Confirm which 4 households on 4th still need check-in (mobility + pets)', confidence: 72, confidence_basis: 'group_knowledge', burden_expected: 2, created_by: pid })
  const n2 = addNode(kit.id, { description: 'Walk 4th from north end and note water depth at each curb + any blocked storm drains', confidence: 58, confidence_basis: 'guess', burden_expected: 4, created_by: pid })
  const n3 = addNode(kit.id, { parent_id: n2.id, description: 'Report exact inches at the dip by the big sycamore before 18:40', confidence: 65, confidence_basis: 'observed', burden_expected: 2, created_by: pid })

  // Simulate some action + abandonment + recovery clarity
  updateNode(kit.id, n2.id, { status: 'active', burden_actual: 5 })
  const abandoned = abandonNode(kit.id, n3.id, 'The sycamore dip is already under 11 inches and rising fast. Foot access gone. We cannot safely reach it before dark without a canoe or waders we do not have.', pid)

  const card = addClarityCard(kit.id, {
    node_id: n3.id,
    participant_id: pid,
    prompt_variant: 'historical',
    situation: 'Trying to get precise depth at the sycamore dip on 4th',
    what_became_clear: 'Personal vehicles and boots are not sufficient once water crosses ~9 inches at that low point. We need either a high-clearance vehicle from outside or a small boat/raft for the lowest two houses.',
    assumption_falsified: 'Assumption that "we can always walk or drive the block" was already false by the time we opened the kit.',
    surprise_level: 4
  })

  // A couple more nodes to make the graph interesting
  addNode(kit.id, { description: 'Reach upstream neighbor (Patel) by phone or runner and ask for their curb reading + any upstream debris report', confidence: 48, confidence_basis: 'rumor', burden_expected: 3, created_by: pid })
  addNode(kit.id, { description: 'Agree a single text template + time window for the 3 households we can still reach tonight', confidence: 81, confidence_basis: 'group_knowledge', burden_expected: 1, created_by: pid })

  return { kit, nodes: getNodesForKit(kit.id), clarity: [card] }
}
